import { DialogWithForm } from '@strata/react-dialog';
import { InlineMessage } from '@strata/react-inline-message';
import { TextInputFormik } from '@strata/react-inputs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestMultiSelectFormik, SuggestSingleSelectFormik } from '@strata/react-selects';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import * as Yup from 'yup';

import { handleError } from 'services/ErrorManagement';
import { get } from 'services/Http';
import {
    ConditionType,
    ConditionTypeNameMap,
    getSelectedProductType,
    ProductType,
} from 'services/RouteRuleHelpers';

const ConditionTypeOptions = Object.values(ConditionType).map((type) => ({
    value: type,
    name: ConditionTypeNameMap[type],
}));

const getFieldsForConditionType = ({ formik, conditionOptionsMap }) => {
    switch (formik.values.condition_type) {
        case ConditionType.PLACE_OF_SERVICE:
        case ConditionType.PRODUCT_LIST:
            return (
                <SuggestMultiSelectFormik
                    id="value"
                    label={ConditionTypeNameMap[formik.values.condition_type]}
                    required
                    values={conditionOptionsMap[formik.values.condition_type]}
                    isLoading={!conditionOptionsMap[formik.values.condition_type]}
                    onChange={(newValues) =>
                        formik.setFieldValue(
                            'value',
                            newValues?.map(({ value }) => value),
                        )
                    }
                    selectedItems={conditionOptionsMap[formik.values.condition_type]?.filter(
                        ({ value }) => formik.values.value?.includes(value),
                    )}
                />
            );
        case ConditionType.PRESCRIBER:
            return <TextInputFormik id="value" label="NPI" required />;
        case ConditionType.PRODUCT:
            return (
                <>
                    <SuggestSingleSelectFormik
                        id="value.product_service_oid"
                        label="Product"
                        required
                        values={conditionOptionsMap[formik.values.condition_type]}
                        isLoading={!conditionOptionsMap[formik.values.condition_type]}
                        selectedItem={conditionOptionsMap[formik.values.condition_type]?.find(
                            ({ value }) => value === formik.values.value?.product_service_oid,
                        )}
                        onChange={(newVal) =>
                            formik.setFieldValue('value', { product_service_oid: newVal?.value })
                        }
                    />
                    <RadioButtonGroupFormik
                        name="value.is_brand"
                        label="Product Type"
                        required
                        options={Object.values(ProductType)}
                        selectedValue={getSelectedProductType(formik.values.value?.is_brand)}
                        onChange={(newVal) =>
                            formik.setFieldValue('value.is_brand', newVal === ProductType.BRAND)
                        }
                    />
                </>
            );
        case ConditionType.PRODUCT_TYPE:
            return (
                <RadioButtonGroupFormik
                    name="value"
                    label={ConditionTypeNameMap[formik.values.condition_type]}
                    required
                    options={Object.values(ProductType)}
                    selectedValue={getSelectedProductType(formik.values.value)}
                    onChange={(newVal) =>
                        formik.setFieldValue('value', newVal === ProductType.BRAND)
                    }
                />
            );
        case ConditionType.SINGLE_SERVICING_SUPPLIER:
            return (
                <InlineMessage
                    intent={InlineMessage.Intent.INFO}
                    label="The previous supplier who serviced all prior referrals."
                />
            );
        case ConditionType.DIAGNOSIS: {
            const selectedDiagnosis = conditionOptionsMap[formik.values.condition_type]?.find(
                ({ value }) => value === formik.values.value,
            );
            return (
                <>
                    <SuggestSingleSelectFormik
                        id="value"
                        label={ConditionTypeNameMap[formik.values.condition_type]}
                        required
                        values={conditionOptionsMap[formik.values.condition_type]}
                        isLoading={!conditionOptionsMap[formik.values.condition_type]}
                        onChange={(newValue) => formik.setFieldValue('value', newValue?.value)}
                        selectedItem={selectedDiagnosis}
                    />
                    {selectedDiagnosis?.codes && (
                        <InlineMessage
                            intent={InlineMessage.Intent.INFO}
                            label={selectedDiagnosis.codes}
                            wrapText
                        />
                    )}
                </>
            );
        }
        default:
            return (
                <SuggestSingleSelectFormik
                    id="value"
                    label={ConditionTypeNameMap[formik.values.condition_type]}
                    required
                    values={conditionOptionsMap[formik.values.condition_type]}
                    isLoading={!conditionOptionsMap[formik.values.condition_type]}
                    onChange={(newValue) => formik.setFieldValue('value', newValue?.value)}
                    selectedItem={conditionOptionsMap[formik.values.condition_type]?.find(
                        ({ value }) => value === formik.values.value,
                    )}
                />
            );
    }
};

export const RouteConditionDialog = ({
    condition = undefined,
    conditionOptionsMap,
    onSave,
    onClose,
}) => {
    const routeRuleFormik = useFormikContext();

    const conditionTypeOptions = useMemo(
        () =>
            ConditionTypeOptions.filter(
                ({ value }) =>
                    !routeRuleFormik.values.conditions.find(
                        (c) =>
                            c.condition_type === value &&
                            c.condition_type !== condition?.condition_type,
                    ),
            ),
        [condition?.condition_type, routeRuleFormik.values.conditions],
    );

    return (
        <Formik
            initialValues={{ condition_type: condition?.condition_type, value: condition?.value }}
            enableReinitialize
            validationSchema={Yup.object({
                condition_type: Yup.string().required(validationStrings.requiredField.errorMessage),
                value: Yup.string()
                    .required(validationStrings.requiredField.errorMessage)
                    .when('condition_type', ([type], schema) => {
                        switch (type) {
                            case ConditionType.PLACE_OF_SERVICE:
                            case ConditionType.PRODUCT_LIST:
                                return Yup.array()
                                    .required(validationStrings.requiredField.errorMessage)
                                    .min(1, validationStrings.requiredField.errorMessage);
                            case ConditionType.PRESCRIBER:
                                return schema.length(10, 'Must be exactly 10 digits');
                            case ConditionType.PRODUCT:
                                return Yup.object({
                                    product_service_oid: Yup.string().required(
                                        validationStrings.requiredField.errorMessage,
                                    ),
                                    is_brand: Yup.bool().required(
                                        validationStrings.requiredField.errorMessage,
                                    ),
                                });
                            case ConditionType.SINGLE_SERVICING_SUPPLIER:
                                return Yup.bool().required(
                                    validationStrings.requiredField.errorMessage,
                                );
                            default:
                                return schema;
                        }
                    }),
            })}
            onSubmit={async (values, { resetForm }) => {
                try {
                    if (values.condition_type === ConditionType.PRESCRIBER) {
                        // Ensure prescriber exists
                        await get(`/reference/prescribers/${values.value}`);
                    }

                    onSave(values);

                    resetForm();
                    onClose();
                } catch (err) {
                    handleError(JSON.parse(err.message ?? '{}')?.detail, err);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle="Add/Edit Condition"
                    primaryButtonLabel="Save"
                    secondaryButtonLabel="Cancel"
                    width={600}
                    isOpen={!!condition}
                    onSecondaryButtonClick={(e) => {
                        formik.handleReset(e);
                        onClose();
                    }}
                    isPrimaryButtonDisabled={
                        !(formik.dirty && formik.isValid) || formik.isSubmitting
                    }
                    dialogContent={
                        <>
                            <SuggestSingleSelectFormik
                                id="condition_type"
                                label="Condition Type"
                                required
                                values={conditionTypeOptions.filter(
                                    ({ value }) =>
                                        !routeRuleFormik.values.conditions.find(
                                            (c) =>
                                                c.condition_type === value &&
                                                c.condition_type !== condition?.condition_type,
                                        ),
                                )}
                                onChange={(newValue) => {
                                    formik.setErrors({}); // Prevents requiredValidationMessage errors when changing validation schemas
                                    formik.setValues({
                                        condition_type: newValue?.value,
                                        value:
                                            newValue?.value ===
                                            ConditionType.SINGLE_SERVICING_SUPPLIER
                                                ? true
                                                : undefined,
                                    });
                                }}
                                selectedItem={conditionTypeOptions.find(
                                    ({ value }) => value === formik.values.condition_type,
                                )}
                            />
                            {formik.values.condition_type &&
                                getFieldsForConditionType({ formik, conditionOptionsMap })}
                        </>
                    }
                />
            )}
        </Formik>
    );
};

RouteConditionDialog.propTypes = {
    condition: PropTypes.shape({
        condition_type: PropTypes.string,
        value: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.bool,
            PropTypes.shape({}),
            PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})])),
        ]),
    }),
    conditionOptionsMap: PropTypes.shape({}).isRequired,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};
